import { StorageKeys } from '../../helpers/localStorage';
import {
  ChartData,
  ChartMetaInfo,
  ChartTemplate,
  ChartTemplateContent,
  IExternalSaveLoadAdapter,
  StudyTemplateData,
  StudyTemplateMetaInfo,
} from './charting_library';

export class SaveLoadAdapter implements IExternalSaveLoadAdapter {
  private chartsKey = StorageKeys.tv_chart;
  private studyTemplatesKey = StorageKeys.tv_chart_study_templates;
  private drawingTemplatesKey = StorageKeys.tv_chart_drawing_templates;
  private chartTemplatesKey = StorageKeys.tv_chart_templates;

  private getStoredData(key: string) {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : [];
  }

  private setStoredData(key: string, data: any) {
    localStorage.setItem(key, JSON.stringify(data));
  }

  getAllCharts(): Promise<ChartMetaInfo[]> {
    return Promise.resolve(this.getStoredData(this.chartsKey));
  }

  async removeChart<T extends number | string>(id: T): Promise<void> {
    const charts = this.getStoredData(this.chartsKey);
    const updatedCharts = charts.filter((chart: ChartData) => chart.id !== id);
    this.setStoredData(this.chartsKey, updatedCharts);
  }

  async saveChart(chartData: ChartData): Promise<string> {
    let charts = this.getStoredData(this.chartsKey);

    if (!chartData.id) {
      chartData.id = Math.random().toString();
    } else {
      charts = charts.filter((chart: ChartData) => chart.id !== chartData.id);
    }

    charts.push(chartData);
    this.setStoredData(this.chartsKey, charts);

    return chartData.id;
  }

  async getChartContent(chartId: number): Promise<string> {
    const charts = this.getStoredData(this.chartsKey);

    const chart = charts.find(
      (chart: ChartData) => chart.id === (chartId as any)
    );

    if (chart) {
      return chart.content;
    } else {
      console.error('error');
      return Promise.reject();
    }
  }

  getAllStudyTemplates(): Promise<StudyTemplateMetaInfo[]> {
    return Promise.resolve(this.getStoredData(this.studyTemplatesKey));
  }

  async removeStudyTemplate(
    studyTemplateInfo: StudyTemplateMetaInfo
  ): Promise<void> {
    const templates = this.getStoredData(this.studyTemplatesKey);
    const updatedTemplates = templates.filter(
      (template: StudyTemplateData) => template.name !== studyTemplateInfo.name
    );
    this.setStoredData(this.studyTemplatesKey, updatedTemplates);
  }

  async getStudyTemplateContent(
    studyTemplateInfo: StudyTemplateMetaInfo
  ): Promise<string> {
    const templates = this.getStoredData(this.studyTemplatesKey);
    const template = templates.find(
      (template: StudyTemplateData) => template.name === studyTemplateInfo.name
    );

    if (template) {
      return template.content;
    } else {
      console.error('st: error');
      return Promise.reject();
    }
  }

  async saveStudyTemplate(studyTemplateData: StudyTemplateData): Promise<void> {
    let templates = this.getStoredData(this.studyTemplatesKey);
    templates = templates.filter(
      (template: StudyTemplateData) => template.name !== studyTemplateData.name
    );
    templates.push(studyTemplateData);
    this.setStoredData(this.studyTemplatesKey, templates);
  }

  async removeDrawingTemplate(
    toolName: string,
    templateName: string
  ): Promise<void> {
    const templates = this.getStoredData(this.drawingTemplatesKey);
    const updatedTemplates = templates.filter(
      (template: any) =>
        template.name === templateName && template.toolName === toolName
    );
    this.setStoredData(this.drawingTemplatesKey, updatedTemplates);
  }

  async loadDrawingTemplate(
    toolName: string,
    templateName: string
  ): Promise<string> {
    const templates = this.getStoredData(this.drawingTemplatesKey);
    const template = templates.find(
      (template: any) =>
        template.name === templateName && template.toolName === toolName
    );

    if (template) {
      return template.content;
    } else {
      console.error('drawing: error');
      return Promise.reject();
    }
  }

  async saveDrawingTemplate(
    toolName: string,
    templateName: string,
    content: string
  ): Promise<void> {
    let templates = this.getStoredData(this.drawingTemplatesKey);
    templates = templates.filter(
      (template: any) =>
        template.name !== templateName && template.toolName === toolName
    );
    templates.push({ name: templateName, toolName, content });
    this.setStoredData(this.drawingTemplatesKey, templates);
  }

  async getDrawingTemplates(toolName: string): Promise<string[]> {
    const templates = this.getStoredData(this.drawingTemplatesKey);
    return templates
      .filter((template: any) => template.toolName === toolName)
      .map((template: any) => template.name);
  }

  async getAllChartTemplates(): Promise<string[]> {
    const chartTemplates = this.getStoredData(this.chartTemplatesKey);
    return chartTemplates.map((x: any) => x.name);
  }

  async saveChartTemplate(
    newName: string,
    content: ChartTemplateContent
  ): Promise<void> {
    const chartTemplates = this.getStoredData(this.chartTemplatesKey);
    const existingTemplate = chartTemplates.find(
      (x: any) => x.name === newName
    );

    if (existingTemplate) {
      existingTemplate.content = content;
    } else {
      chartTemplates.push({ name: newName, content });
    }

    this.setStoredData(this.chartTemplatesKey, chartTemplates);
  }

  async removeChartTemplate(templateName: string): Promise<void> {
    let chartTemplates = this.getStoredData(this.chartTemplatesKey);
    chartTemplates = chartTemplates.filter((x: any) => x.name !== templateName);
    this.setStoredData(this.chartTemplatesKey, chartTemplates);
  }

  async getChartTemplateContent(templateName: string): Promise<ChartTemplate> {
    const chartTemplates = this.getStoredData(this.chartTemplatesKey);
    const theme: any = {};

    const content = chartTemplates.find(
      (x: any) => x.name === templateName
    )?.content;

    if (content) {
      theme.content = structuredClone(content);
    }

    return theme;
  }
}
